// src/pages/PipsCreatureKeeper.tsx
// 🔧 FIXED: Refresh now properly reloads all data
// ✅ FIXED: Victory screen won't show during starter selection
// ✅ FIXED: Better error handling throughout
// ✅ FIXED: Duplicate closing tag removed
// ✅ FIXED: Removed shield emoji from title

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonSpinner,
  IonButton,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonBadge,
  IonIcon,
  IonButtons,
  IonAlert,
  IonBackButton,
  IonToast
} from '@ionic/react';
import { refresh, settings, arrowBack } from 'ionicons/icons';
import { usePipsCreatureKeeper } from '../hooks/usePipsCreatureKeeper';
import { logger } from '../utils/logger';
import { soundService } from '../services/soundService';
import Tutorial from '../components/games/pipsCreatureKeeper/Tutorial';
import KeeperNamePrompt from '../components/games/pipsCreatureKeeper/KeeperNamePrompt';
import StarterSelector from '../components/games/pipsCreatureKeeper/StarterSelector';
import HabitatGrid from '../components/games/pipsCreatureKeeper/HabitatGrid';
import TerritoryList from '../components/games/pipsCreatureKeeper/TerritoryList';
import FieldJournal from '../components/games/pipsCreatureKeeper/FieldJournal';
import VictoryScreen from '../components/games/pipsCreatureKeeper/VictoryScreen';

const PipsCreatureKeeper: React.FC = () => {
  const {
    currentGame,
    gameData,
    loading,
    userId,
    createNewGame,
    selectStarter,
    setSelectedCompanion,
    setSelectedHabitatIndex,
    refreshGameData,
    reloadHabitatPlacements,
    resetGame,
    signOut
  } = usePipsCreatureKeeper();

  const [showTutorial, setShowTutorial] = useState(!currentGame);
  const [showKeeperNamePrompt, setShowKeeperNamePrompt] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'habitats' | 'explore' | 'journal'>('habitats');
  const [resetting, setResetting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Track if we've shown victory screen for this session
  const [victoryShown, setVictoryShown] = useState(false);

  // Load Pip's Creature Keeper sounds on mount
  useEffect(() => {
    soundService.loadSounds({
      'creature-catch': '/assets/sounds/pips-creature-keeper/catch.mp3',
      'habitat-place': '/assets/sounds/pips-creature-keeper/place.mp3',
      'sanctuary-unlock': '/assets/sounds/pips-creature-keeper/unlock.mp3',
    });
  }, []);

  // Memoize selected companion
  const selectedCompanion = useMemo(() => {
    return gameData.selectedCompanion;
  }, [gameData.selectedCompanion]);

  // Memoize stats
  const stats = useMemo(() => ({
    captureCount: gameData.myCreatures.length,
    discoveryCount: gameData.fieldJournal.length,
    unlockedHabitats: gameData.unlockedHabitats.length,
    unlockedTerritories: gameData.unlockedTerritories.length
  }), [gameData.myCreatures.length, gameData.fieldJournal.length, gameData.unlockedHabitats.length, gameData.unlockedTerritories.length]);

  // Check for victory condition - only if game is fully initialized
  useEffect(() => {
    // Explicit check: only show victory if player has actually started playing
    const hasStartedPlaying = gameData.myCreatures.length > 0;
    const hasCompletedCollection = gameData.myCreatures.length >= 50;

    if (currentGame &&
        hasStartedPlaying &&
        hasCompletedCollection &&
        !victoryShown &&
        !loading) {
      logger.log('🏆 VICTORY! All 50 creatures captured!');
      setShowVictory(true);
      setVictoryShown(true);
    }
  }, [currentGame, gameData.myCreatures.length, victoryShown, loading]);

  // Reset victory shown flag when game resets
  useEffect(() => {
    if (!currentGame) {
      setVictoryShown(false);
    }
  }, [currentGame]);

  const handleRefresh = useCallback(async () => {
    if (!currentGame || refreshing) return;

    logger.log('🔄 Refreshing game data...');
    setRefreshing(true);
    setRefreshProgress('Starting refresh...');

    try {
      await refreshGameData((step: string) => {
        setRefreshProgress(step);
      });
      setToastMessage('Game data refreshed!');
      setShowToast(true);
      logger.log('✅ Refresh complete');
    } catch (error) {
      logger.error('❌ Refresh failed:', error);
      setToastMessage('Failed to refresh data');
      setShowToast(true);
    } finally {
      setRefreshing(false);
      setRefreshProgress('');
    }
  }, [currentGame, refreshGameData, refreshing]);

  const handleReset = useCallback(async () => {
    const confirmed = window.confirm(
      "Are you sure you want to reset Pip's Creature Keeper? This will delete all your progress and cannot be undone."
    );

    if (confirmed) {
      setResetting(true);
      try {
        await resetGame();
        setShowTutorial(true);
        setVictoryShown(false); // Reset victory flag
      } catch (error) {
        logger.error('Failed to reset game:', error);
        alert('Failed to reset game. Please try again.');
      } finally {
        setResetting(false);
      }
    }
  }, [resetGame]);

  const handleTutorialComplete = useCallback(async () => {
    logger.log('✅ Tutorial complete, showing keeper name prompt');
    setShowTutorial(false);
    setShowKeeperNamePrompt(true);
  }, []);

  const handleKeeperNameSubmit = useCallback(async (name: string) => {
    logger.log('🗝️ Creating game with keeper name:', name);
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      // 10 second timeout - users shouldn't wait longer than this
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout - this is taking longer than expected')), 10000);
      });

      await Promise.race([createNewGame(name), timeoutPromise]);

      logger.log('✅ Game created successfully');
      setShowKeeperNamePrompt(false);
    } catch (error: any) {
      logger.error('❌ Failed to create game:', error);
      // Note: Cookie warnings like "__cf_bm" are harmless and don't affect functionality
      if (error.message?.includes('timeout')) {
        throw new Error('Connection is slow or timed out. Please try again.');
      } else if (error.message?.includes('fetch') || error.message?.includes('network')) {
        throw new Error('Network error - please check your connection and try again.');
      }
      throw error;
    }
  }, [userId, createNewGame]);

  const handleStarterSelect = useCallback(async (creatureId: number) => {
    logger.log('🎯 Selecting starter creature:', creatureId);
    try {
      await selectStarter(creatureId);
      logger.log('✅ Starter selected, refreshing data...');
      if (currentGame) {
        await refreshGameData();
      }
    } catch (error) {
      logger.error('❌ Error in handleStarterSelect:', error);
      throw error;
    }
  }, [selectStarter, currentGame, refreshGameData]);

  const handleNavigateToHabitats = useCallback(() => {
    setSelectedTab('habitats');
  }, []);

  const handleVictoryClose = useCallback(() => {
    setShowVictory(false);
  }, []);

  // Loading state
  if (loading) {
    return (
      <IonPage>
        <IonContent className="ion-padding ion-text-center">
          <div style={{ marginTop: '50%' }}>
            <IonSpinner name="crescent" />
            <p>Loading Sanctuary...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Tutorial flow
  if (showTutorial && !currentGame) {
    return (
      <IonPage>
        <Tutorial
          isOpen={true}
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialComplete}
        />
        <KeeperNamePrompt
          isOpen={showKeeperNamePrompt}
          onSubmit={handleKeeperNameSubmit}
        />
      </IonPage>
    );
  }

  // Keeper name prompt
  if (showKeeperNamePrompt) {
    return (
      <IonPage>
        <KeeperNamePrompt
          isOpen={true}
          onSubmit={handleKeeperNameSubmit}
        />
      </IonPage>
    );
  }

  // Starter selection - DON'T show victory screen during this
  if (currentGame && gameData && gameData.myCreatures.length === 0) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/games" icon={arrowBack} text="Games" />
            </IonButtons>
            <IonTitle>Pip's Creature Keeper</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <StarterSelector
            creatures={gameData.creatures}
            onSelect={handleStarterSelect}
          />
        </IonContent>
      </IonPage>
    );
  }

  // Main game view
  if (!currentGame || !gameData) {
    return (
      <IonPage>
        <IonContent className="ion-padding ion-text-center">
          <div style={{ marginTop: '50%' }}>
            <IonSpinner name="crescent" />
            <p>Loading game data...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      {showVictory && (
        <VictoryScreen
          keeperName={currentGame.player_name}
          totalCreatures={gameData.myCreatures.length}
          onClose={handleVictoryClose}
        />
      )}

      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/games" icon={arrowBack} text="Games" />
          </IonButtons>
          <IonTitle>Pip's Creature Keeper</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? (
                <IonSpinner name="crescent" style={{ width: '20px', height: '20px' }} />
              ) : (
                <IonIcon icon={refresh} slot="icon-only" />
              )}
            </IonButton>
            <IonButton onClick={() => setShowSettings(true)}>
              <IonIcon icon={settings} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>

        {/* Refresh Progress Banner */}
        {refreshing && refreshProgress && (
          <IonToolbar color="light" style={{ minHeight: '36px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '13px',
              color: '#666'
            }}>
              <IonSpinner name="dots" style={{ width: '16px', height: '16px' }} />
              <span>{refreshProgress}</span>
            </div>
          </IonToolbar>
        )}

        {/* Tab Segment */}
        <IonToolbar>
          <IonSegment value={selectedTab} onIonChange={(e) => setSelectedTab(e.detail.value as any)}>
            <IonSegmentButton value="habitats">
              <IonLabel>Habitats</IonLabel>
              {gameData.myCreatures.length > 0 && (
                <IonBadge color="success">{gameData.myCreatures.length}</IonBadge>
              )}
            </IonSegmentButton>
            <IonSegmentButton value="explore">
              <IonLabel>Explore</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="journal">
              <IonLabel>Journal</IonLabel>
              {gameData.fieldJournal.length > 0 && (
                <IonBadge color="warning">{gameData.fieldJournal.length}/50</IonBadge>
              )}
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Habitats Tab */}
        {selectedTab === 'habitats' && (
          <HabitatGrid
            habitats={gameData.habitats}
            unlockedHabitats={gameData.unlockedHabitats}
            habitatPlacements={gameData.habitatPlacements}
            myCreatures={gameData.myCreatures}
            creatures={gameData.creatures}
            typeCompatibility={gameData.typeCompatibility}
            selectedCompanion={selectedCompanion}
            onCompanionSelect={setSelectedCompanion}
            onRefresh={reloadHabitatPlacements}
            onNavigateToExplore={() => setSelectedTab('explore')}
            selectedHabitatIndex={gameData.selectedHabitatIndex}
            onHabitatIndexChange={setSelectedHabitatIndex}
          />
        )}

        {/* Explore Tab */}
        {selectedTab === 'explore' && (
          <TerritoryList
            territories={gameData.territories}
            availableCreatures={gameData.creatures}
            myCreatures={gameData.myCreatures}
            selectedCompanion={selectedCompanion}
            gameId={currentGame.id}
            habitatPlacements={gameData.habitatPlacements}
            gameData={gameData}
            onRefresh={handleRefresh}
            onNavigateToHabitats={handleNavigateToHabitats}
          />
        )}

        {/* Journal Tab */}
        {selectedTab === 'journal' && (
          <FieldJournal
            creatures={gameData.creatures}
            fieldJournal={gameData.fieldJournal}
            myCreatures={gameData.myCreatures}
            typeCompatibility={gameData.typeCompatibility}
          />
        )}

        {/* Settings Alert */}
        <IonAlert
          isOpen={showSettings}
          onDidDismiss={() => setShowSettings(false)}
          header="⚙️ Settings"
          message={`Keeper: ${currentGame.player_name}\n\nCaptured: ${stats.captureCount}/50 | Discovered: ${stats.discoveryCount}/50\n\nHabitats Unlocked: ${stats.unlockedHabitats} | Territories: ${stats.unlockedTerritories}`}
          buttons={[
            {
              text: 'Refresh Data',
              handler: () => {
                handleRefresh();
                setShowSettings(false);
              }
            },
            {
              text: 'Reset Game',
              role: 'destructive',
              handler: async () => {
                await handleReset();
                setShowSettings(false);
              }
            },
            {
              text: 'Sign Out',
              handler: () => {
                signOut();
                setShowSettings(false);
              }
            },
            {
              text: 'Cancel',
              role: 'cancel'
            }
          ]}
        />

        {/* Toast for feedback */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          position="bottom"
          aria-live="polite"
        />
      </IonContent>
    </IonPage>
  );
};

export default PipsCreatureKeeper;